import { useState } from "react";
import axios from "axios";
import { Lock, Loader2, Search } from "lucide-react";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [key, setKey] = useState("");
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchAppointments = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await axios.get(`${API}/appointments`, { headers: { "x-admin-key": key } });
      setItems(res.data);
    } catch (e) {
      setError(e?.response?.status === 401 ? "Invalid admin key." : "Could not load appointments.");
      setItems(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-page" className="pt-[72px] min-h-screen">
      <Seo title="Appointments Dashboard | Kamana Physiotherapy" description="Private appointments dashboard." />
      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-16">
        <p className="mono-label text-gold-deep">Private Area</p>
        <h1 className="font-display text-3xl lg:text-4xl font-semibold text-teal-brand mt-3">
          Appointments Dashboard
        </h1>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl">
          <div className="relative flex-1">
            <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-inkmuted" />
            <input
              type="password"
              data-testid="admin-key-input"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Enter admin key"
              className="w-full rounded-full border border-teal-brand/15 bg-white pl-11 pr-4 py-3.5 text-sm outline-none focus:border-teal-brand transition-colors"
            />
          </div>
          <button
            onClick={fetchAppointments}
            disabled={loading}
            data-testid="admin-fetch-button"
            className="inline-flex items-center justify-center gap-2 bg-teal-brand text-alabaster font-semibold text-sm rounded-full px-7 py-3.5 hover:bg-teal-deep transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            View Bookings
          </button>
        </div>

        {error && (
          <p data-testid="admin-error" className="mt-4 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3 max-w-xl">
            {error}
          </p>
        )}

        {items && (
          <div data-testid="admin-appointments-table" className="mt-10 overflow-x-auto rounded-2xl border border-teal-brand/10 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-teal-soft text-teal-brand">
                <tr>
                  {["Date Requested", "Patient", "Phone", "Concern", "Preferred", "Slot", "Notes", "ID"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 font-semibold text-xs whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-inkmuted">
                      No appointments yet.
                    </td>
                  </tr>
                )}
                {items.map((a) => (
                  <tr key={a.id} className="border-t border-teal-brand/5" data-testid={`admin-row-${a.id.slice(0, 8)}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-inkmuted">{a.created_at?.slice(0, 10)}</td>
                    <td className="px-4 py-3 font-medium text-teal-brand whitespace-nowrap">{a.name}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{a.phone}</td>
                    <td className="px-4 py-3 text-inkmuted">{a.condition}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{a.preferred_date}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-inkmuted">{a.time_slot}</td>
                    <td className="px-4 py-3 text-inkmuted max-w-[220px] truncate">{a.notes || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gold-deep">{a.id.slice(0, 8).toUpperCase()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
